import { Get } from './Get'
import { Post } from './Post'
import { Put } from './Put'
import { ApiHelpers, APIHosts } from './ApiHelpers'

export type Client = {
  id: number
  clientId: string
  clientName: string
  description?: string
  enabled: boolean
  // Auth
  protocolType: 'oidc'
  requireClientSecret: boolean
  requirePkce: boolean
  requireConsent: boolean
  enableLocalLogin: boolean
  // Token
  accessTokenLifetime: number
  identityTokenLifetime: number
  refreshTokenExpiration: number
  refreshTokenUsage: number
  absoluteRefreshTokenLifetime: number
  slidingRefreshTokenLifetime: number
  allowOfflineAccess: boolean
  allowAccessTokensViaBrowser: boolean
  updateAccessTokenClaimsOnRefresh: boolean
  // URLs
  redirectUris: string[]
  postLogoutRedirectUris: string[]
  allowedCorsOrigins: string[]
  // Permissions
  allowedGrantTypes: string[]
  allowedScopes: string[]
  // Optional advanced
  claims?: { id: number; type: string; value: string }[]
  properties?: { id: number; key: string; value: string }[]
  // Metadata
  createdAt?: string
  updated?: string
  lastAccessed?: string
}

const DEFAULT_CLIENT_CONFIG = {
  allowOfflineAccess: true,
  allowAccessTokensViaBrowser: true,
  updateAccessTokenClaimsOnRefresh: true,
  refreshTokenExpiration: 0,
  requireClientSecret: false,
  enabled: true,
  allowedGrantTypes: ['password', 'refresh_token'],
  allowedScopes: ['offline_access', 'API', 'openid'],
} as const

const DEFAULT_UPDATE_CLIENT_CONFIG = {
  allowOfflineAccess: true,
  allowAccessTokensViaBrowser: true,
  updateAccessTokenClaimsOnRefresh: true,
  refreshTokenExpiration: 0,
  requireClientSecret: false,
}

export type CreateClientInput = {
  clientId: string
  clientName: string
}

export type UpdateClientInput = {
  clientId: string
  clientName: string
  allowOfflineAccess: boolean
  allowAccessTokensViaBrowser: boolean
  updateAccessTokenClaimsOnRefresh: boolean
  refreshTokenExpiration: number
  enabled: boolean
  requireClientSecret: boolean
}

class ClientServiceController extends ApiHelpers {
  ApiHost = APIHosts.LicenseManager

  public Get = {
    getAllClients: async () => {
      const baseUrl = this.getHost(this.ApiHost)
      const url = `${baseUrl}/clients`
      return Get(url)
    },
    getClientById: (clientId: string) => {
      const baseUrl = this.getHost(this.ApiHost)
      const url = `${baseUrl}/clients/${clientId}`
      return Get(url)
    },
  }

  public Post = {
    createNewClient: (clientInfo: CreateClientInput) => {
      const baseUrl = this.getHost(this.ApiHost)

      return Post(`${baseUrl}/clients`, {
        ...DEFAULT_CLIENT_CONFIG,
        ...clientInfo,
      })
    },
    createNewProduct: (name: string, clientId: string) => {
      const baseUrl = this.getHost(this.ApiHost)
      return Post(`${baseUrl}/products`, {
        name,
        ClientId: clientId,
      })
    },
  }

  public Put = {
    updateClientInfo: (clientId: string, updateInfo: UpdateClientInput) => {
      const baseUrl = this.getHost(this.ApiHost)
      return Put(`${baseUrl}/clients/${clientId}`, {
        ...DEFAULT_UPDATE_CLIENT_CONFIG,
        ...updateInfo,
        clientId,
      })
    },
  }
}

export const ClientsService = new ClientServiceController()
